import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AppDialog,
  Button,
  Checkbox,
  MiniLoader,
  Select,
  TextInput,
  type AppDialogAction,
  type SelectOption,
} from '../Common'
import { useNls } from '../../nls/nlsProvider'
import {
  admitEduEnquiry,
  type EduEnquiry,
} from '../../services/eduEnquiryService'
// Cite: createGuardianCode from src/services/eduLearnerService.ts:121-125
import { createGuardianCode } from '../../services/eduLearnerService'
import { listEduFeePlans, type EduFeePlanData } from '../../services/eduFeeService'
import { JoinCodeSlips, type JoinCodeSlipItem } from '../JoinCodes/JoinCodeSlips'
import { buildClassOptions, type ClassOption } from './learnersTabHelpers'
import type { TimelineElement } from '../../types/timelineElement'
import { mapEduServerError, eduServerErrorCode } from './classesBuilderHelpers'
import { useWorkspace } from '../../context/useWorkspace'
import { useAuth } from '../../context/useAuth'
import { isWorkspaceOwner } from '../../utils/workspaceOwners'
import { getWhatsappInviteInfo } from '../../services/whatsappService'
import './admissionsTab.css'

const makeRequestId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `req_${Date.now()}_${Math.random().toString(36).slice(2)}`

export interface AdmitEnquiryDialogProps {
  isOpen: boolean
  onClose: () => void
  enquiry: EduEnquiry | null
  workspaceId: string
  workspaceName?: string
  elements: TimelineElement[]
  onSuccess: (learnerId: string) => void
}

function findRootId(nodeId: string, elements: TimelineElement[]): string {
  const map = new Map(elements.map((el) => [el.id, el]))
  let curr = map.get(nodeId)
  while (curr && curr.parentId) {
    const parent = map.get(curr.parentId)
    if (!parent) break
    curr = parent
  }
  return curr ? curr.id : nodeId
}

interface CredentialsState {
  learnerId: string
  joinCodeId?: string
  code?: string
  pin?: string
  expiresAtMs?: number
  codeAlreadyIssued?: boolean
}

export const AdmitEnquiryDialog = ({
  isOpen,
  onClose,
  enquiry,
  workspaceId,
  workspaceName,
  elements,
  onSuccess,
}: AdmitEnquiryDialogProps) => {
  const { t } = useNls()
  const { workspaces, activeRole } = useWorkspace()
  const { currentUser } = useAuth()
  const currentWs = workspaces.find((w) => w.id === workspaceId)
  const isAdmin = activeRole === 'ADMIN' || activeRole === 'OWNER' || isWorkspaceOwner(currentWs, currentUser?.uid)

  const [whatsappNumberE164, setWhatsappNumberE164] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    if (isOpen && workspaceId && isAdmin) {
      getWhatsappInviteInfo(workspaceId)
        .then((info) => {
          if (isMounted) setWhatsappNumberE164(info.sendingNumberE164)
        })
        .catch(() => {
          if (isMounted) setWhatsappNumberE164(null)
        })
    }
    return () => {
      isMounted = false
      setWhatsappNumberE164(null)
    }
  }, [isOpen, workspaceId, isAdmin])

  const [loadingFeePlans, setLoadingFeePlans] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [issuingNewCode, setIssuingNewCode] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Form fields
  const [targetNodeId, setTargetNodeId] = useState<string>('')
  const [isMinor, setIsMinor] = useState<boolean>(true)
  const [institutionAttests, setInstitutionAttests] = useState<boolean>(false)
  const [guardianEmail, setGuardianEmail] = useState<string>('')
  const [guardianPhone, setGuardianPhone] = useState<string>('')
  const [selectedFeePlanId, setSelectedFeePlanId] = useState<string>('')

  // Fee plans
  const [feePlans, setFeePlans] = useState<EduFeePlanData[]>([])

  // One-time credentials display (never persisted beyond dialog)
  const [credentials, setCredentials] = useState<CredentialsState | null>(null)

  const classOptions: ClassOption[] = useMemo(() => buildClassOptions(elements), [elements])

  const targetRootId = useMemo(() => {
    if (!targetNodeId) return ''
    return findRootId(targetNodeId, elements)
  }, [targetNodeId, elements])

  // Initialize form from enquiry
  useEffect(() => {
    if (!isOpen || !enquiry) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset credentials when dialog closes
      setCredentials(null)
      setError(null)
      setInstitutionAttests(false)
      setSelectedFeePlanId('')
      return
    }

    const initialNode = enquiry.interestNodeId || classOptions[0]?.id || ''
    setTargetNodeId(initialNode)
    setIsMinor(Boolean(enquiry.learner.isMinor))
    setInstitutionAttests(false)
    setGuardianEmail(enquiry.contact.email || '')
    setGuardianPhone(enquiry.contact.phone || '')
    setSelectedFeePlanId('')
    setError(null)
    setCredentials(null)
  }, [isOpen, enquiry, classOptions])

  // Load fee plans for root and filter by kind
  useEffect(() => {
    if (!isOpen || !workspaceId || !targetRootId || !enquiry) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initialize loading indicator
    setLoadingFeePlans(true)
    let isMounted = true

    const loadPlans = async () => {
      try {
        const res = await listEduFeePlans({
          workspaceId,
          rootId: targetRootId,
          status: 'ACTIVE',
        })
        if (!isMounted) return
        const allPlans = res.plans || []
        const isTeaching = enquiry.interestUseCase === 'TEACHING'
        // Filter: FEE for Teaching, PACK / MEMBERSHIP for Training; root matches target
        const filtered = allPlans.filter((p) => {
          if (p.rootId !== targetRootId) return false
          if (isTeaching) return p.kind === 'FEE'
          return p.kind === 'PACK' || p.kind === 'MEMBERSHIP'
        })
        setFeePlans(filtered)
      } catch (err) {
        if (!isMounted) return
        console.warn('Failed to load fee plans', err)
      } finally {
        if (isMounted) setLoadingFeePlans(false)
      }
    }

    loadPlans()

    return () => {
      isMounted = false
    }
  }, [isOpen, workspaceId, targetRootId, enquiry])

  const classSelectOptions: SelectOption<string>[] = useMemo(() => {
    return classOptions.map((c) => ({
      value: c.id,
      label: c.label,
    }))
  }, [classOptions])

  const feePlanOptions: SelectOption<string>[] = useMemo(() => {
    const opts: SelectOption<string>[] = [
      { value: '', label: t('NLSEduAdmissionsAdmitNoFeePlan') },
    ]
    for (const p of feePlans) {
      opts.push({
        value: p.id,
        label: `${p.name} (${p.kind})`,
      })
    }
    return opts
  }, [feePlans, t])

  const handleClose = useCallback(() => {
    // Clear credentials completely from state on close
    setCredentials(null)
    setError(null)
    onClose()
  }, [onClose])

  const handleSubmit = async () => {
    if (!enquiry || !workspaceId || !targetNodeId) return
    if (isMinor && !institutionAttests) {
      setError(t('NLSEducationLearnersAttestationRequired'))
      return
    }

    setError(null)
    setSubmitting(true)
    const requestId = makeRequestId()

    try {
      const res = await admitEduEnquiry({
        workspaceId,
        enquiryId: enquiry.id,
        nodeId: targetNodeId,
        isMinor,
        institutionAttests: isMinor ? institutionAttests : undefined,
        guardianEmail: guardianEmail.trim() || undefined,
        guardianPhone: guardianPhone.trim() || undefined,
        feePlanId: selectedFeePlanId || undefined,
        requestId,
      })

      setCredentials({
        learnerId: res.learnerId,
        joinCodeId: res.joinCodeId,
        code: res.code,
        pin: res.pin,
        expiresAtMs: res.expiresAtMs,
        codeAlreadyIssued: res.codeAlreadyIssued,
      })

      onSuccess(res.learnerId)
    } catch (err) {
      const code = eduServerErrorCode(err)
      if (code === 'FEE_PLAN_WRONG_ROOT') {
        setError(t('NLSEduAdmissionsErrorFeePlanWrongRoot'))
      } else if (code === 'FEE_PLAN_MISMATCH') {
        setError(t('NLSEduAdmissionsErrorFeePlanMismatch'))
      } else if (code === 'CONSENT_ATTESTATION_REQUIRED') {
        setError(t('NLSEduAdmissionsErrorConsentAttestationRequired'))
      } else {
        setError(mapEduServerError(err, t))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleIssueNewCode = async () => {
    if (!credentials?.learnerId) return
    setError(null)
    setIssuingNewCode(true)
    try {
      const newCreds = await createGuardianCode(credentials.learnerId)
      setCredentials({
        learnerId: credentials.learnerId,
        joinCodeId: newCreds.joinCodeId,
        code: newCreds.code,
        pin: newCreds.pin,
        expiresAtMs: newCreds.expiresAtMs,
        codeAlreadyIssued: false,
      })
    } catch (err) {
      setError(mapEduServerError(err, t))
    } finally {
      setIssuingNewCode(false)
    }
  }

  const slips: JoinCodeSlipItem[] = useMemo(() => {
    if (!credentials?.code || !credentials?.pin) return []
    return [
      {
        key: `${credentials.learnerId}-${credentials.joinCodeId || credentials.code}`,
        displayName: enquiry?.learner.name || 'Learner',
        code: credentials.code,
        pin: credentials.pin,
        expiresAtMs: credentials.expiresAtMs || 0,
      },
    ]
  }, [credentials, enquiry?.learner.name])

  const isFormValid = Boolean(
    targetNodeId && (!isMinor || institutionAttests),
  )

  const actions: AppDialogAction[] = credentials
    ? credentials.codeAlreadyIssued && !credentials.code
      ? [
          {
            label: t('NLSEduEnquiryDetailCancel'),
            variant: 'secondary',
            onClick: handleClose,
          },
        ]
      : [
          {
            label: t('NLSEduAdmissionsAdmitPrintBtn'),
            variant: 'secondary',
            onClick: () => window.print(),
            testId: 'admit-print-slip-btn',
          },
          {
            label: t('NLSEduAdmissionsAdmitDoneBtn'),
            variant: 'primary',
            onClick: handleClose,
            testId: 'admit-done-btn',
          },
        ]
    : [
        {
          label: t('NLSEduEnquiryDetailCancel'),
          variant: 'secondary',
          disabled: submitting,
          onClick: handleClose,
        },
        {
          label: submitting
            ? t('NLSEduAdmissionsSaving')
            : t('NLSEduAdmissionsAdmitSubmit'),
          variant: 'primary',
          disabled: !isFormValid || submitting,
          onClick: handleSubmit,
          testId: 'admit-submit-btn',
        },
      ]

  return (
    <AppDialog
      open={isOpen}
      onDismiss={handleClose}
      title={credentials ? t('NLSEduAdmissionsAdmitCredentialsTitle') : t('NLSEduAdmissionsAdmitTitle')}
      data-testid="admit-enquiry-dialog"
      actions={actions}
    >
      <div className="admissionsDialogContent">
        {error && (
          <div
            className="admissionsInlineError"
            role="alert"
            data-testid="admit-dialog-error"
          >
            {error}
          </div>
        )}

        {credentials ? (
          <div className="admissionsCredentialsView" data-testid="admit-credentials-view">
            {credentials.codeAlreadyIssued && !credentials.code ? (
              <div className="admissionsCodeAlreadyIssued">
                <p data-testid="code-already-issued-msg">
                  {t('NLSEduAdmissionsAdmitCodeAlreadyIssued')}
                </p>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleIssueNewCode}
                  disabled={issuingNewCode}
                  data-testid="issue-new-code-btn"
                >
                  {issuingNewCode
                    ? t('NLSEduAdmissionsSaving')
                    : t('NLSEduAdmissionsAdmitIssueNewCodeBtn')}
                </Button>
              </div>
            ) : (
              <>
                <div
                  className="admissionsCredentialsWarning"
                  data-testid="credentials-warning-banner"
                >
                  {t('NLSEduAdmissionsAdmitCredentialsWarning')}
                </div>

                <div className="admissionsSlipsContainer" data-testid="admissions-slips-container">
                  <JoinCodeSlips
                    slips={slips}
                    workspaceName={workspaceName || ''}
                    whatsappNumberE164={whatsappNumberE164}
                  />
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="admissionsDialogForm" data-testid="admit-form">
            <div className="admissionsFormField">
              <label className="admissionsFieldLabel">
                {t('NLSEduAdmissionsAdmitTargetClass')}
              </label>
              <Select
                ariaLabel={t('NLSEduAdmissionsAdmitTargetClass')}
                value={targetNodeId}
                options={classSelectOptions}
                onChange={setTargetNodeId}
                data-testid="admit-target-class-select"
              />
            </div>

            <div className="admissionsFormField">
              <Checkbox
                id="admitIsMinor"
                label={t('NLSEduAdmissionsLearnerIsMinor')}
                checked={isMinor}
                onChange={setIsMinor}
              />
            </div>

            {isMinor && (
              <div className="admissionsFormField" data-testid="admit-attestation-field">
                {/* Cite consent text: reuse NLSEducationLearnersAttestationLabel from ED1 learner-create flow */}
                <Checkbox
                  id="admitInstitutionAttests"
                  label={t('NLSEducationLearnersAttestationLabel')}
                  checked={institutionAttests}
                  onChange={setInstitutionAttests}
                />
              </div>
            )}

            <div className="admissionsFormField">
              <label className="admissionsFieldLabel">
                {t('NLSEduAdmissionsAdmitGuardianEmail')}
              </label>
              <TextInput
                aria-label={t('NLSEduAdmissionsAdmitGuardianEmail')}
                value={guardianEmail}
                onChange={setGuardianEmail}
                data-testid="admit-guardian-email-input"
              />
            </div>

            <div className="admissionsFormField">
              <label className="admissionsFieldLabel">
                {t('NLSEduAdmissionsAdmitGuardianPhone')}
              </label>
              <TextInput
                aria-label={t('NLSEduAdmissionsAdmitGuardianPhone')}
                value={guardianPhone}
                onChange={setGuardianPhone}
                data-testid="admit-guardian-phone-input"
              />
            </div>

            <div className="admissionsFormField">
              <label className="admissionsFieldLabel">
                {t('NLSEduAdmissionsAdmitFeePlan')}
              </label>
              {loadingFeePlans ? (
                <MiniLoader />
              ) : (
                <Select
                  ariaLabel={t('NLSEduAdmissionsAdmitFeePlan')}
                  value={selectedFeePlanId}
                  options={feePlanOptions}
                  onChange={setSelectedFeePlanId}
                  data-testid="admit-fee-plan-select"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </AppDialog>
  )
}
